import React, { useEffect, useState } from 'react'
import { Box, Card, Grid, TextField } from '@mui/material'
import DatePicker from '@mui/lab/DatePicker'
import { AgencySelect } from 'src/admin/scenes/entities/agencies/components'
import { WorkstationSelect } from 'src/admin/scenes/entities/stations/components'
import { Filter as FilterModel } from '../../models'
import { Location } from 'src/admin/scenes/entities/agencies/models'
import { Workstation } from 'src/admin/scenes/entities/stations/models'
import { useAppTranslation } from 'src/admin/core/hooks'
import { WorkstationProductSelect } from 'src/admin/core'
import { WorkstationProductsEnum } from 'src/core/models'

export const initialFilters = {
  productId: 'HORSE_RACES',
  locationId: '',
  stationId: '',
  salesTime: new Date(),
  wagerAmountTotal: undefined,
  ticketNumber: '',
  providerTicket: '',
}

/**
 * Validates lottery ticket number format for LOTTERY_TICKET product
 * Expected format: XXXX-XXXXXXX-XX-XX (4 parts separated by hyphen)
 */
function validateLotteryTicketNumber(ticketNumber: string): boolean {
  const parts = ticketNumber.split("-");
  return parts.length === 4 &&
    parts[0].length > 0 &&   // Parte 1: al menos 1 dígito
    parts[1].length > 0 &&   // Parte 2: al menos 1 dígito
    parts[2].length === 2 && // Parte 3: exactamente 2 dígitos
    parts[3].length === 2;   // Parte 4: exactamente 2 dígitos
}

const Filters: React.FC<{ onChange: (filters: FilterModel) => void }> = ({ onChange }) => {
  const [filters, setFilters] = useState<FilterModel>(initialFilters)
  const [ticketError, setTicketError] = useState<string>('')
  const { label } = useAppTranslation()

  useEffect(() => {
    // Only trigger onChange when specific fields change
    onChange(filters);
  }, [filters.salesTime, filters.locationId, filters.stationId, filters.ticketNumber, filters.wagerAmountTotal, filters.productId]);

  const onTextfieldChange = (value: any, field: string) => {
    const newFilters = { ...filters };
    
    if (field === 'ticketNumber') {
      newFilters.ticketNumber = value;
      
      if (filters.productId === 'LOTTERY_TICKET') {
        // Validate lottery ticket number format
        if (value && !validateLotteryTicketNumber(value)) {
          setTicketError('Formato inválido. Use: XXXX-XXXXXXX-XX-XX');
        } else {
          setTicketError('');
        }
      } else if (value) {
        // Validate ticket number for other products
        const isNumeric = /^\d+$/.test(value);
        const MIN_TICKET_VALUE = 1000000;
        const MAX_TICKET_VALUE = '9223372036854775806'; // Using string to avoid precision loss
        
        if (!isNumeric || 
            parseInt(value) < MIN_TICKET_VALUE || 
            (isNumeric && value.length > MAX_TICKET_VALUE.length) ||
            (isNumeric && value.length === MAX_TICKET_VALUE.length && value > MAX_TICKET_VALUE)) {
          setTicketError('La secuencia del ticket es invalida');
        } else {
          setTicketError('');
        }
      } else {
        setTicketError('');
      }
    } else if (field === 'wagerAmountTotal') {
      newFilters.wagerAmountTotal = Number.parseFloat(value);
    }
    
    setFilters(newFilters);
  }

  return (
    <Card
      sx={{
        p: 1,
      }}
    >
      <Box
        sx={{
          p: 1,
        }}
      >
        <Grid container spacing={3} mb={2}>
          <Grid item xs={12} md={6} lg={6}>
            <DatePicker
              inputFormat="dd/MM/yyyy"
              value={filters.salesTime}
              onChange={(newValue) =>
                setFilters({
                  ...filters,
                  salesTime: newValue,
                })
              }
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={6}>
            <WorkstationProductSelect
              disableClearable={false}
              selectDefaultProduct={false}
              value={filters.productId as WorkstationProductsEnum}
              onChange={(val) => {
                const newProductId = val || 'HORSE_RACES';
                // Clear ticket error when product changes
                setTicketError('');
                // Re-validate ticket number with new product rules if there's a value
                if (filters.ticketNumber) {
                  if (newProductId === 'LOTTERY_TICKET') {
                    if (!validateLotteryTicketNumber(filters.ticketNumber)) {
                      setTicketError('Formato inválido. Use: XXXX-XXXXXXX-XX-XX');
                    }
                  } else {
                    const isNumeric = /^\d+$/.test(filters.ticketNumber);
                    const MIN_TICKET_VALUE = 1000000;
                    const MAX_TICKET_VALUE = '9223372036854775806';
                    if (!isNumeric || 
                        parseInt(filters.ticketNumber) < MIN_TICKET_VALUE || 
                        (isNumeric && filters.ticketNumber.length > MAX_TICKET_VALUE.length) ||
                        (isNumeric && filters.ticketNumber.length === MAX_TICKET_VALUE.length && filters.ticketNumber > MAX_TICKET_VALUE)) {
                      setTicketError('La secuencia del ticket es invalida');
                    }
                  }
                }
                setFilters({
                  ...filters,
                  productId: newProductId,
                });
              }}
              getDefaultValue={(val) => {
                setFilters({
                  ...filters,
                  productId: val || 'HORSE_RACES',
                });
              }}
            />
          </Grid>
        </Grid>
        <Grid container spacing={3}>   
          <Grid item xs={12} md={6} lg={6}>
            <AgencySelect
              onChangeValue={(value: Location) =>
                setFilters({
                  ...filters,
                  locationId: value?.id,
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={6} lg={6}>
            <WorkstationSelect
              locationId={filters.locationId ? filters.locationId : ''}
              onChangeValue={(value: Workstation) =>
                setFilters({
                  ...filters,
                  stationId: value?.id,
                })
              }
            />
          </Grid>
        </Grid>
      </Box>
      <Box
        sx={{
          p: 1,
        }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={6}>
            <TextField
              fullWidth
              autoComplete="off"
              value={filters.ticketNumber}
              label={label('TICKET')}
              placeholder={label('TICKET')}
              InputLabelProps={{
                shrink: true,
              }}
              onChange={(event) => onTextfieldChange(event.target.value, 'ticketNumber')}
              error={!!ticketError}
              helperText={ticketError}
            />
          </Grid>
          {/* <Grid item xs={12} md={4} lg={4}>
            <TextField
              fullWidth
              value={filters.providerTicket}
              label={label('PROVEEDOR')}
              placeholder={label('PROVEEDOR')}
              InputLabelProps={{
                shrink: true,
              }}
              onChange={(event) => onTextfieldChange(event.target.value, 'providerTicket')}
            />
          </Grid> */}
          <Grid item xs={12} md={6} lg={6}>
            <TextField
              fullWidth
              autoComplete="off"
              value={filters.wagerAmountTotal}
              label={label('MONTO')}
              placeholder={label('MONTO')}
              type="number"
              InputLabelProps={{
                shrink: true,
              }}
              onChange={(event) => onTextfieldChange(event.target.value, 'wagerAmountTotal')}
            />
          </Grid>
        </Grid>
      </Box>
    </Card>
  )
}

export default Filters
